import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Stack,
  Text,
  VStack
} from '@chakra-ui/react';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { useAdminAuthContext } from '../../contexts/AdminAuthProvider';
import {
  InvalidVerificationCodeError,
  NotAnAccountError
} from '../../utils/clients/EstudiAuthClient';
import estudiLogo from '../../resources/estudi.jpeg';

const EmailSchema = Yup.object().shape({
  email: Yup.string().email('Email inválido').required('Requerido')
});

const CodeSchema = Yup.object().shape({
  code: Yup.string().required('Requerido')
});

interface EmailFormValues {
  email: string;
}

interface CodeFormValues {
  code: string;
}

const AdminLogin = () => {
  const { requestCode, submitCode } = useAdminAuthContext();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const handleEmailSubmit = async (values: EmailFormValues) => {
    setError(undefined);

    try {
      await requestCode(values.email);
      setEmail(values.email);
    } catch (exception) {
      setError(
        exception instanceof NotAnAccountError
          ? 'Ese email no tiene acceso al backoffice'
          : 'No se pudo enviar el código, intentá de nuevo'
      );
    }
  };

  const handleCodeSubmit = async (values: CodeFormValues) => {
    setError(undefined);

    try {
      await submitCode(email, values.code);
    } catch (exception) {
      setError(
        exception instanceof InvalidVerificationCodeError
          ? 'Código inválido o vencido'
          : 'No se pudo verificar el código, intentá de nuevo'
      );
    }
  };

  return (
    <React.Fragment>
      <Container maxW="container.sm">
        <Stack mb={5} mt={5}>
          <Image src={estudiLogo} alt="Estudi Logo" width="60px" alignSelf="center" />
        </Stack>
      </Container>
      <Container centerContent maxW="container.sm" py={5}>
        <Box borderWidth="1px" borderRadius="lg" p={8} width="100%">
          <Stack spacing={4}>
            <Text fontSize="2xl" fontWeight="bold" textAlign="center">
              Backoffice
            </Text>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {!email && (
              <Formik
                initialValues={{ email: '' }}
                validationSchema={EmailSchema}
                onSubmit={handleEmailSubmit}>
                {({ errors, touched, isSubmitting, values }: FormikProps<EmailFormValues>) => (
                  <Form>
                    <VStack spacing={4}>
                      <Field name="email">
                        {({ field }: { field: any }) => (
                          <FormControl isRequired isInvalid={!!(errors.email && touched.email)}>
                            <FormLabel>Email</FormLabel>
                            <Input {...field} type="email" placeholder="vos@estudi.com.ar" />
                            <FormErrorMessage>{errors.email}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Button
                        colorScheme="blue"
                        type="submit"
                        width="full"
                        isLoading={isSubmitting}
                        loadingText="Enviando..."
                        isDisabled={!values.email || isSubmitting}>
                        Enviarme un código
                      </Button>
                    </VStack>
                  </Form>
                )}
              </Formik>
            )}

            {email && (
              <Formik
                initialValues={{ code: '' }}
                validationSchema={CodeSchema}
                onSubmit={handleCodeSubmit}>
                {({ errors, touched, isSubmitting, values }: FormikProps<CodeFormValues>) => (
                  <Form>
                    <VStack spacing={4}>
                      <Text fontSize="sm" color="gray.600">
                        Te mandamos un código a <b>{email}</b>. Vence en 10 minutos.
                      </Text>
                      <Field name="code">
                        {({ field }: { field: any }) => (
                          <FormControl isRequired isInvalid={!!(errors.code && touched.code)}>
                            <FormLabel>Código</FormLabel>
                            <Input {...field} placeholder="123456" autoFocus />
                            <FormErrorMessage>{errors.code}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Button
                        colorScheme="blue"
                        type="submit"
                        width="full"
                        isLoading={isSubmitting}
                        loadingText="Verificando..."
                        isDisabled={!values.code || isSubmitting}>
                        Entrar
                      </Button>
                      <Button variant="link" size="sm" onClick={() => setEmail('')}>
                        Usar otro email
                      </Button>
                    </VStack>
                  </Form>
                )}
              </Formik>
            )}
          </Stack>
        </Box>
      </Container>
    </React.Fragment>
  );
};

export default AdminLogin;
